/**
 * useBreakpoint — Responsive breakpoint hooks for VianBills
 *
 * Breakpoint map (matches tailwind.config.js screens):
 *   base  →  0 – 479px    (small phone, e.g. iPhone SE)
 *   xs    →  480 – 639px  (large phone, e.g. iPhone 14 Pro)
 *   sm    →  640 – 767px  (large phone landscape / small tablet)
 *   md    →  768 – 1023px (tablet portrait, e.g. iPad mini)
 *   lg    →  1024 – 1279px (tablet landscape / small laptop)
 *   xl    →  1280 – 1535px (desktop)
 *   2xl   →  1536px+      (wide desktop)
 *
 * Usage:
 *   const isMobile  = useIsMobile();   // true below 768px
 *   const isTablet  = useIsTablet();   // true 768–1023px
 *   const isDesktop = useIsDesktop();  // true 1024px+
 *   const bp        = useBreakpoint(); // 'base' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
 *
 * All hooks use matchMedia listeners — they only re-render when a
 * breakpoint boundary is crossed, not on every resize event.
 *
 * SSR-safe: defaults to desktop (xl) when window is not available so
 * server-rendered or pre-hydration renders show the desktop layout.
 */

import { useState, useEffect } from "react";

// ─── Internal helper ────────────────────────────────────────────────────────

/** Returns a matchMedia query result and subscribes to changes. */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const handler = (e) => setMatches(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

// ─── Named breakpoint hooks ──────────────────────────────────────────────────

/** True when viewport width < 768px (phones). */
export function useIsMobile() {
  return useMediaQuery("(max-width: 767px)");
}

/** True when viewport width is 768px–1023px (tablets). */
export function useIsTablet() {
  return useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
}

/** True when viewport width ≥ 1024px (laptops + desktops). */
export function useIsDesktop() {
  return useMediaQuery("(min-width: 1024px)");
}

/** True when viewport width < 1024px (phones + tablets). */
export function useIsTouchDevice() {
  return useMediaQuery("(max-width: 1023px)");
}

// ─── Current breakpoint string ───────────────────────────────────────────────

const BREAKPOINT_QUERIES = [
  { name: "2xl", query: "(min-width: 1536px)" },
  { name: "xl",  query: "(min-width: 1280px)" },
  { name: "lg",  query: "(min-width: 1024px)" },
  { name: "md",  query: "(min-width: 768px)"  },
  { name: "sm",  query: "(min-width: 640px)"  },
  { name: "xs",  query: "(min-width: 480px)"  },
];

function getCurrentBreakpoint() {
  if (typeof window === "undefined") return "xl"; // SSR default
  for (const { name, query } of BREAKPOINT_QUERIES) {
    if (window.matchMedia(query).matches) return name;
  }
  return "base";
}

/**
 * Returns the current Tailwind breakpoint name.
 * @returns {'base' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'}
 */
export function useBreakpoint() {
  const [bp, setBp] = useState(getCurrentBreakpoint);

  useEffect(() => {
    const mediaQueries = BREAKPOINT_QUERIES.map(({ query }) =>
      window.matchMedia(query)
    );
    const handler = () => setBp(getCurrentBreakpoint());
    mediaQueries.forEach((mq) => mq.addEventListener("change", handler));
    return () =>
      mediaQueries.forEach((mq) => mq.removeEventListener("change", handler));
  }, []);

  return bp;
}

// ─── Convenience helpers ─────────────────────────────────────────────────────

/**
 * Returns an object with all breakpoint booleans at once.
 * Use when you need multiple breakpoints in one component to avoid
 * multiple hook calls.
 *
 * @returns {{ isMobile: boolean, isTablet: boolean, isDesktop: boolean,
 *             isTouchDevice: boolean, bp: string }}
 */
export function useResponsive() {
  const bp = useBreakpoint();
  return {
    bp,
    isMobile:      bp === "base" || bp === "xs" || bp === "sm",
    isTablet:      bp === "md",
    isDesktop:     bp === "lg" || bp === "xl" || bp === "2xl",
    isTouchDevice: bp !== "lg" && bp !== "xl" && bp !== "2xl",
  };
}
